import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../../framework/BaseWebComponentController';
import { ImportOptionsWebComponent } from '../component/ImportOptionsWebComponent';
import { ImportOptionsView } from '../view/ImportOptionsView';
import { templateMdrImportOptions } from '../view/template';
import { RadioEvent } from '../../../../../ui/gzRadio/types';
import { MdrContactDetailsSource } from '../../common/types';
import { ImportOptionsViewData, ImportOptionsLayout } from '../types/ImportOptionsViewData';
import { ListEvents } from '../../../../../ui/gzSingleSelectList/types';
import { CompanyActionContext } from '../../../types/CompanyActionContext';
import { FormGroupChild } from '../../../../../ui/gzForm/formGroup/types/form-group-children';
import { GzHardCodedFormField } from '../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { isEmpty } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

export class ImportOptionsController extends BaseWebComponentController {
  public component: ImportOptionsWebComponent;
  public view: ImportOptionsView;
  public viewConstructor: typeof ImportOptionsView;

  private formFilledSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public formFilled$: Observable<boolean> = this.formFilledSubject.pipe(distinctUntilChanged());

  constructor(component: ImportOptionsWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: ImportOptionsView,
      viewTemplate: templateMdrImportOptions,
    });
  }

  /**
   * Determines the appropriate layout based on viewData
   *
   * @param viewData The view data containing context and flags
   * @returns The appropriate ImportOptionsLayout
   */
  public getLayout(viewData: ImportOptionsViewData): ImportOptionsLayout {
    if (viewData.shouldHidePartnerImport) {
      return ImportOptionsLayout.SELF_ACCOUNT_ONLY;
    }

    if (viewData.context === CompanyActionContext.Create) {
      return ImportOptionsLayout.SELF_COMPANY_RADIO_GROUP;
    }

    if (viewData.context === CompanyActionContext.Edit) {
      return ImportOptionsLayout.ANY_COMPANY_RADIO_GROUP;
    }

    if (viewData.context === CompanyActionContext.MyCompany) {
      return ImportOptionsLayout.SELF_ACCOUNT_ONLY;
    }

    // Default fallback
    return ImportOptionsLayout.SELF_COMPANY_RADIO_GROUP;
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$radioGroup,
        event: RadioEvent.SELECTION_CHANGED,
        callback: this.onImportOptionChange,
      },
      {
        element: this.view.dom.$partnerSelector,
        event: ListEvents.SELECTION_CHANGE,
        callback: this.onImportPartnerChange,
      },
      {
        element: this.view.dom.$accountSelector,
        event: ListEvents.SELECTION_CHANGE,
        callback: this.onImportAccountChange,
      },
      {
        element: this.view.dom.$accountSelectorFromSelf,
        event: ListEvents.SELECTION_CHANGE,
        callback: this.onImportAccountFromSelfChange,
      },
    ];
  }

  async getDataForView(): Promise<ImportOptionsViewData> {
    const defaultViewData: ImportOptionsViewData = {
      context: CompanyActionContext.Create,
    };

    const viewData = this.component.viewData ?? defaultViewData;

    viewData.layout = this.getLayout(viewData);

    return viewData;
  }

  public updateFormFilledSubject(): void {
    if (!this.component.viewData) {
      return;
    }

    const layout = this.getLayout(this.component.viewData);
    const radioValue = parseInt(this.view.dom.$radioGroup.value as string, 10);
    const hasSelectedPartnerSource = radioValue === MdrContactDetailsSource.PARTNER_CONTACT;
    const hasSelectedAccountSource = radioValue === MdrContactDetailsSource.GZ_ACCOUNT;
    let hasFilledOptionPartner = false;
    let hasFilledOptionAccount = false;

    switch (layout) {
      case ImportOptionsLayout.SELF_COMPANY_RADIO_GROUP:
        hasFilledOptionPartner = hasSelectedPartnerSource;
        hasFilledOptionAccount = hasSelectedAccountSource && !isEmpty(this.view.dom.$accountSelector.value);
        break;
      case ImportOptionsLayout.ANY_COMPANY_RADIO_GROUP:
        hasFilledOptionPartner = hasSelectedPartnerSource && !isEmpty(this.view.dom.$partnerSelector.value);
        hasFilledOptionAccount = hasSelectedAccountSource && !isEmpty(this.view.dom.$accountSelector.value);
        break;
      case ImportOptionsLayout.SELF_ACCOUNT_ONLY:
        hasFilledOptionAccount = !isEmpty(this.view.dom.$accountSelectorFromSelf.value);
        break;
    }

    this.formFilledSubject.next(hasFilledOptionPartner || hasFilledOptionAccount);
  }

  private onImportOptionChange(): void {
    if (!this.component.viewData) {
      return;
    }

    const layout = this.getLayout(this.component.viewData);
    const selectedOption: number = parseInt(this.view.dom.$radioGroup.value as string, 10);

    switch (selectedOption) {
      case MdrContactDetailsSource.PARTNER_CONTACT:
        let companyIdFormChild: FormGroupChild;

        if (layout !== ImportOptionsLayout.SELF_COMPANY_RADIO_GROUP) {
          this.view.dom.$partnerSelector.disabled = false;
          companyIdFormChild = this.view.dom.$partnerSelector;
        } else {
          this.view.dom.$partnerSelector.disabled = true;
          this.view.dom.$partnerSelector.value = '';
          companyIdFormChild = new GzHardCodedFormField<string>(this.dependencies.getUser().company.id);
        }

        this.view.dom.$accountSelector.disabled = true;
        this.view.dom.$accountSelector.value = '';

        if (this.view.formGroup) {
          this.view.formGroup.removeFormChild(['importAccount']);
          this.view.formGroup.addFormChild(['importAccount', 'companyId'], companyIdFormChild);
        }

        break;
      case MdrContactDetailsSource.GZ_ACCOUNT:
        this.view.dom.$partnerSelector.disabled = true;
        this.view.dom.$partnerSelector.value = '';

        this.view.dom.$accountSelector.disabled = false;

        if (this.view.formGroup) {
          this.view.formGroup.removeFormChild(['importAccount']);
          this.view.formGroup.addFormChild(['importAccount', 'accountId'], this.view.dom.$accountSelector);
        }

        break;
      default:
        this.view.dom.$partnerSelector.disabled = true;
        this.view.dom.$partnerSelector.value = '';

        this.view.dom.$accountSelector.disabled = true;
        this.view.dom.$accountSelector.value = '';

        if (this.view.formGroup) {
          this.view.formGroup.removeFormChild(['importAccount']);
        }
    }

    this.updateFormFilledSubject();
  }

  private onImportPartnerChange(): void {
    this.updateFormFilledSubject();
  }

  private onImportAccountChange(): void {
    this.updateFormFilledSubject();
  }

  private onImportAccountFromSelfChange(event: WebComponentEvent): void {
    if (isEmpty(event.detail.selectedValue)) {
      this.view.dom.$accountSelectorFromSelf.resetField();
    }

    this.updateFormFilledSubject();
  }
}
